import { PromptRun, PromptRunProcessingStatus, VideoStatus } from '../services/api';
import { getRunProjectionState } from './runScopedVideoStatus';

export interface PromptRunProgressSummary {
  runId: string;
  promptName: string;
  status: string;
  executedAt: string;
  runVideoIds: string[];
  totalSegments: number;
  successfulSegments: number;
  inProgressSegments: number;
  failedSegments: number;
  pendingSegments: number;
  totalVideos: number;
  successfulVideos: number;
  inProgressVideos: number;
  failedVideos: number;
  cancelledVideos: number;
  totalMedia: number;
  successfulMedia: number;
  inProgressMedia: number;
  failedMedia: number;
  cancelledMedia: number;
  totalAudios: number;
  totalImages: number;
  successfulAudios: number;
  successfulImages: number;
  failedAudios: number;
  failedImages: number;
  cancelledAudios: number;
  cancelledImages: number;
  /** Active run.progress events report logical media, not segment progress. */
  hasLiveMediaProgress?: boolean;
  source: 'fallback' | 'aggregated';
}

const PROCESSING_PHASE_ORDER = ['queued', 'preparing', 'extracting', 'preparing_segments', 'indexing', 'synthesizing'];

/** Report the earliest phase with active work, without estimating a completion percentage. */
export const getPromptRunActivityPhase = (run: Pick<PromptRun, 'activity_counts'>): string | undefined =>
  PROCESSING_PHASE_ORDER.find((phase) => Number(run.activity_counts?.[phase]) > 0);

const toSafeNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
};

const clampCount = (value: number): number => Math.max(0, Math.floor(value));

const FAILED_RUN_STATUSES = new Set([
  'failed',
  'completed_with_failures',
]);

const IN_PROGRESS_RUN_STATUSES = new Set([
  'pending',
  'processing',
  'queued',
  'running',
  'started',
  'in_progress',
]);

const COMPLETED_RUN_STATUSES = new Set([
  'completed',
  'succeeded',
  'success',
  'done',
]);

export const isPromptRunSearchableStatus = (status?: string | null): boolean => {
  const normalized = String(status || '').toLowerCase();
  return normalized === 'completed' || normalized === 'completed_with_failures';
};

const getDisplayRunStatus = (run: Pick<PromptRun, 'status'>): string => (
  String(run.status || 'pending').toLowerCase()
);

const classifyRunVideoState = (
  runStatus: PromptRunProcessingStatus, parentRunStatus: string,
): 'successful' | 'in_progress' | 'failed' | 'cancelled' => {
  return getRunProjectionState(runStatus, parentRunStatus) || 'in_progress';
};

export const sanitizeRunVideoIds = (run: Pick<PromptRun, 'run_context'> | null | undefined): string[] => {
  const rawVideoIds = run?.run_context?.video_ids;
  if (!Array.isArray(rawVideoIds)) {
    return [];
  }

  const deduped = new Set<string>();
  rawVideoIds.forEach((value) => {
    if (typeof value !== 'string') {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    deduped.add(trimmed);
  });

  return Array.from(deduped);
};

export const getPromptRunIndexId = (
  run: Pick<PromptRun, 'run_context'> | null | undefined,
): string | null => {
  const context = run?.run_context || {};
  const directIndexId = context.index_id;
  if (typeof directIndexId === 'string' && directIndexId.trim()) {
    return directIndexId.trim();
  }
  const target = context.target;
  if (
    target
    && typeof target === 'object'
    && typeof target.index_id === 'string'
    && target.index_id.trim()
  ) {
    return target.index_id.trim();
  }
  return null;
};

export const clampSegmentMarkerCount = (totalSegments: number, maxMarkers: number = 80): number => {
  const normalizedTotal = clampCount(toSafeNumber(totalSegments));
  if (normalizedTotal <= 0) {
    return 0;
  }
  const normalizedMax = Math.max(2, clampCount(toSafeNumber(maxMarkers)));
  return Math.min(normalizedTotal, normalizedMax);
};

export const sortPromptRunsByExecutionDesc = (runs: PromptRun[]): PromptRun[] => {
  const parseTime = (value?: string | null): number => {
    if (!value) return 0;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return [...runs].sort((left, right) => parseTime(right.executed_at) - parseTime(left.executed_at));
};

export const getPromptRunFallbackSummary = (run: PromptRun): PromptRunProgressSummary => {
  const displayStatus = getDisplayRunStatus(run);
  const totalVideos = clampCount(toSafeNumber(run.total_videos));
  const successfulVideos = clampCount(toSafeNumber(run.completed_videos));
  const partialVideos = clampCount(toSafeNumber(run.partial_videos));
  const cancelledVideos = clampCount(toSafeNumber(run.cancelled_videos));
  const failedVideos = clampCount(toSafeNumber(run.failed_videos)) + partialVideos;
  const inProgressVideos = Math.max(totalVideos - successfulVideos - failedVideos - cancelledVideos, 0);

  const totalAudios = clampCount(toSafeNumber(run.total_audios));
  const totalImages = clampCount(toSafeNumber(run.total_images));
  const successfulAudios = clampCount(toSafeNumber(run.completed_audios));
  const successfulImages = clampCount(toSafeNumber(run.completed_images));
  const partialAudios = clampCount(toSafeNumber(run.partial_audios));
  const partialImages = clampCount(toSafeNumber(run.partial_images));
  const cancelledAudios = clampCount(toSafeNumber(run.cancelled_audios));
  const cancelledImages = clampCount(toSafeNumber(run.cancelled_images));
  const failedAudios = clampCount(toSafeNumber(run.failed_audios)) + partialAudios;
  const failedImages = clampCount(toSafeNumber(run.failed_images)) + partialImages;

  const inProgressAudios = Math.max(totalAudios - successfulAudios - failedAudios - cancelledAudios, 0);
  const inProgressImages = Math.max(totalImages - successfulImages - failedImages - cancelledImages, 0);

  let totalMedia = totalVideos + totalAudios + totalImages;
  let successfulMedia = successfulVideos + successfulAudios + successfulImages;
  let failedMedia = failedVideos + failedAudios + failedImages;
  const cancelledMedia = cancelledVideos + cancelledAudios + cancelledImages;
  let inProgressMedia = inProgressVideos + inProgressAudios + inProgressImages;

  // The worker emits cumulative outcomes as each media item completes, but
  // persists media aggregates after the worker pool drains. These existing SSE
  // counters include reused media and every media type; do not reinterpret them
  // as videos/segments or infer successful work from an admission reuse count.
  const eventCounts = run as PromptRun & {
    total_count?: number;
    completed_count?: number;
    partial_count?: number;
    failed_count?: number;
  };
  const hasLiveMediaProgress = run.status === 'processing'
    && [eventCounts.total_count, eventCounts.completed_count, eventCounts.partial_count, eventCounts.failed_count]
      .every((value) => typeof value === 'number' && Number.isFinite(value));
  if (hasLiveMediaProgress) {
    totalMedia = clampCount(eventCounts.total_count!);
    successfulMedia = Math.min(totalMedia, clampCount(eventCounts.completed_count!));
    failedMedia = Math.min(
      Math.max(totalMedia - successfulMedia - cancelledMedia, 0),
      clampCount(eventCounts.failed_count!) + clampCount(eventCounts.partial_count!),
    );
    inProgressMedia = Math.max(totalMedia - successfulMedia - failedMedia - cancelledMedia, 0);
  }

  const totalSegments = Math.max(
    clampCount(toSafeNumber(run.total_segments)),
    clampCount(toSafeNumber(run.completed_segments))
  );
  const successfulSegments = clampCount(toSafeNumber(run.completed_segments));

  const normalizedRunStatus = String(run.status || '').toLowerCase();
  let failedSegments = 0;
  let inProgressSegments = 0;

  if (totalSegments > 0) {
    const remainingSegments = Math.max(totalSegments - successfulSegments, 0);
    if (remainingSegments > 0) {
      const isFailedState = FAILED_RUN_STATUSES.has(normalizedRunStatus) || normalizedRunStatus.includes('fail');
      const isInProgressState = IN_PROGRESS_RUN_STATUSES.has(normalizedRunStatus);
      const isCompletedState = COMPLETED_RUN_STATUSES.has(normalizedRunStatus) || normalizedRunStatus.includes('complete');

      if (isFailedState || isCompletedState) {
        failedSegments = remainingSegments;
      } else if (isInProgressState) {
        inProgressSegments = remainingSegments;
      } else {
        inProgressSegments = remainingSegments;
      }
    }
  }

  return {
    runId: run.run_id,
    promptName: run.prompt_name || run.prompt_id || 'Unknown Extraction Engine',
    status: displayStatus,
    executedAt: run.executed_at,
    runVideoIds: sanitizeRunVideoIds(run),
    totalSegments,
    successfulSegments,
    inProgressSegments,
    failedSegments,
    pendingSegments: inProgressSegments,
    totalVideos,
    successfulVideos,
    inProgressVideos,
    failedVideos,
    cancelledVideos,
    totalMedia,
    successfulMedia,
    inProgressMedia,
    failedMedia,
    cancelledMedia,
    totalAudios,
    totalImages,
    successfulAudios,
    successfulImages,
    failedAudios,
    failedImages,
    cancelledAudios,
    cancelledImages,
    hasLiveMediaProgress,
    source: 'fallback',
  };
};

export const aggregatePromptRunSummaryFromVideoStatuses = (
  run: PromptRun,
  videoStatuses: VideoStatus[]
): PromptRunProgressSummary => {
  const fallback = getPromptRunFallbackSummary(run);

  let totalSegments = 0;
  let successfulSegments = 0;
  let failedSegments = 0;
  let pendingSegments = 0;
  let processingSegments = 0;

  let successfulVideos = 0;
  let failedVideos = 0;
  let inProgressVideos = 0;
  let cancelledVideos = 0;
  let matchedVideos = 0;

  videoStatuses.forEach((videoStatus) => {
    const runStatus = (videoStatus.processing_status || []).find(
      (item) => item.run_id === run.run_id
    );

    if (!runStatus) {
      return;
    }

    matchedVideos += 1;

    totalSegments += clampCount(toSafeNumber(runStatus.total_segments));
    successfulSegments += clampCount(toSafeNumber(runStatus.successful_segments));
    failedSegments += clampCount(toSafeNumber(runStatus.failed_segments));
    pendingSegments += clampCount(toSafeNumber(runStatus.pending_segments));
    processingSegments += clampCount(toSafeNumber(runStatus.processing_segments));

    const videoState = classifyRunVideoState(runStatus, run.status);
    if (videoState === 'successful') {
      successfulVideos += 1;
    } else if (videoState === 'cancelled') {
      cancelledVideos += 1;
    } else if (videoState === 'failed') {
      failedVideos += 1;
    } else {
      inProgressVideos += 1;
    }
  });

  if (matchedVideos === 0) {
    return fallback;
  }

  const hasNonVideoMedia = fallback.totalAudios > 0 || fallback.totalImages > 0;
  const runVideoIds = sanitizeRunVideoIds(run);
  const totalVideos = fallback.totalVideos > 0 ? fallback.totalVideos : Math.max(runVideoIds.length, matchedVideos);

  const normalizedSuccessfulVideos = hasNonVideoMedia
    ? fallback.successfulVideos
    : Math.min(successfulVideos, totalVideos);
  const normalizedCancelledVideos = hasNonVideoMedia
    ? fallback.cancelledVideos
    : Math.min(cancelledVideos, Math.max(totalVideos - normalizedSuccessfulVideos, 0));
  const normalizedFailedVideos = hasNonVideoMedia
    ? fallback.failedVideos
    : Math.min(
        failedVideos,
        Math.max(totalVideos - normalizedSuccessfulVideos - normalizedCancelledVideos, 0)
      );
  const normalizedInProgressVideos = hasNonVideoMedia
    ? fallback.inProgressVideos
    : Math.max(
        totalVideos - normalizedSuccessfulVideos - normalizedFailedVideos - normalizedCancelledVideos,
        inProgressVideos
      );

  const totalAudios = fallback.totalAudios;
  const totalImages = fallback.totalImages;
  const successfulAudios = fallback.successfulAudios;
  const successfulImages = fallback.successfulImages;
  const failedAudios = fallback.failedAudios;
  const failedImages = fallback.failedImages;
  const cancelledAudios = fallback.cancelledAudios;
  const cancelledImages = fallback.cancelledImages;
  const inProgressAudios = Math.max(totalAudios - successfulAudios - failedAudios - cancelledAudios, 0);
  const inProgressImages = Math.max(totalImages - successfulImages - failedImages - cancelledImages, 0);

  const totalMedia = totalVideos + totalAudios + totalImages;
  const successfulMedia = normalizedSuccessfulVideos + successfulAudios + successfulImages;
  const failedMedia = normalizedFailedVideos + failedAudios + failedImages;
  const cancelledMedia = normalizedCancelledVideos + cancelledAudios + cancelledImages;
  const inProgressMedia = normalizedInProgressVideos + inProgressAudios + inProgressImages;

  return {
    ...fallback,
    status: getDisplayRunStatus(run) || fallback.status,
    source: 'aggregated',
    hasLiveMediaProgress: false,
    totalSegments,
    successfulSegments,
    failedSegments,
    pendingSegments,
    inProgressSegments: pendingSegments + processingSegments,
    totalVideos,
    successfulVideos: normalizedSuccessfulVideos,
    failedVideos: normalizedFailedVideos,
    inProgressVideos: normalizedInProgressVideos,
    cancelledVideos: normalizedCancelledVideos,
    totalMedia,
    successfulMedia,
    failedMedia,
    inProgressMedia,
    cancelledMedia,
  };
};
